/**
 * WidgetOverflowMenu — GhostIconButton kebab opening Configure… (METRIC only,
 * M4) / Rename / Move-resize / Remove (confirm-gated). Same .pop-in popover
 * idiom as AccountSelect/DashboardSwitcher. Remove uses window.confirm — the
 * FieldEditorModal precedent for this risk tier (removing one widget from a
 * dashboard is trivially undoable by re-adding it, not a cascading delete).
 */
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import { GhostIconButton } from '@/components/GhostIconButton';
import type { DashboardWidgetResponse } from '@/types/domain';

/** GhostIconButton doesn't forward a ref, so recover its rendered <button> to
 * restore focus after the menu closes — matching DashboardSwitcher's own
 * Escape/selection focus-restore idiom (its trigger IS a plain <button>). */
function focusTrigger(wrapper: HTMLDivElement | null) {
  wrapper?.querySelector<HTMLButtonElement>('button')?.focus();
}

function KebabIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="12" cy="19" r="1.8" />
    </svg>
  );
}

const menuItemStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', width: '100%', padding: '8px 10px',
  borderRadius: 'var(--radius-sm)', background: 'transparent', border: 'none', cursor: 'pointer',
  textAlign: 'left', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 500,
  color: 'var(--text)',
};

export interface WidgetOverflowMenuProps {
  widget: DashboardWidgetResponse;
  onRename: () => void;
  onMoveResize: () => void;
  onRemove: () => void;
  /** M4 Configure surface — rendered only when the widget is METRIC-kind. */
  onConfigure?: () => void;
}

export function WidgetOverflowMenu({ widget, onRename, onMoveResize, onRemove, onConfigure }: WidgetOverflowMenuProps) {
  const [open, setOpen] = useState(false);
  // Viewport anchor for the portaled menu (top + distance-from-right), computed
  // from the trigger's rect at open time — the DashboardSwitcher DashboardRowMenu
  // idiom (see the file banner: portaled so RGL's transform/sticky-header
  // stacking context can never trap or clip this menu).
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      // The menu is PORTALED to document.body (escaping the .react-grid-item's
      // transform-positioned stacking context), so it is no longer a DOM child
      // of the wrapper: outside-click must check both refs or selecting a menu
      // item closes the menu on mousedown before its click can fire.
      if (wrapperRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        // Return keyboard focus to the trigger rather than dropping it to <body>.
        focusTrigger(wrapperRef.current);
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function handleRemove() {
    setOpen(false);
    const title = widget.title ?? 'this widget';
    if (!confirm(`Remove "${title}" from this dashboard? You can add it again later.`)) return;
    onRemove();
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <GhostIconButton
        tooltip="Widget options"
        label={`${widget.title ?? 'Widget'} options`}
        onClick={() => {
          const r = wrapperRef.current?.getBoundingClientRect();
          if (r) setAnchor({ top: r.bottom + 4, right: window.innerWidth - r.right });
          setOpen((v) => !v);
        }}
        ariaHasPopup="menu"
        ariaExpanded={open}
      >
        <KebabIcon />
      </GhostIconButton>
      {open && anchor && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label="Widget options"
          className="pop-in"
          style={{
            position: 'fixed', top: anchor.top, right: anchor.right, minWidth: 180,
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-panel)',
            boxShadow: 'var(--shadow-2)', zIndex: 30, padding: 4,
            ['--pop-origin' as unknown as string]: 'top right',
          }}
        >
          {widget.kind === 'METRIC' && onConfigure && (
            <button
              type="button" role="menuitem"
              onClick={() => { setOpen(false); focusTrigger(wrapperRef.current); onConfigure(); }}
              className="focus-ring" style={menuItemStyle}
            >
              Configure…
            </button>
          )}
          <button
            type="button" role="menuitem"
            onClick={() => { setOpen(false); focusTrigger(wrapperRef.current); onRename(); }}
            className="focus-ring" style={menuItemStyle}
          >
            Rename
          </button>
          <button
            type="button" role="menuitem"
            onClick={() => { setOpen(false); focusTrigger(wrapperRef.current); onMoveResize(); }}
            className="focus-ring" style={menuItemStyle}
          >
            Move / resize
          </button>
          <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
          <button
            type="button" role="menuitem"
            onClick={() => { focusTrigger(wrapperRef.current); handleRemove(); }}
            className="focus-ring" style={{ ...menuItemStyle, color: 'var(--sem-red)' }}
          >
            Remove
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}
